import { Model } from 'pinia-orm';
import { Str, Attr, Uid, Num, HasOne, Bool } from 'pinia-orm/decorators';
import { Course } from './Course';
import { User } from 'src/modules/User/models/User';
import type { UserCourseProgress } from 'types/course/course';
import type { ApiUserCourseUserCourse } from 'src/types/contentTypes';
import type { StrapiAttr } from 'src/types/strapi';

/** Scalar field types are derived from the backend schema — see src/types/strapi.ts. */
type UserCourseAttr<K extends keyof ApiUserCourseUserCourse['attributes']> =
  StrapiAttr<ApiUserCourseUserCourse, K>;

export class UserCourse extends Model {
  static override entity = 'user_courses';

  @Uid() declare id: number;
  @Str(null) declare documentId: string | null;

  // Strapi types these as DateTime (Date | string); over REST they always
  // arrive as ISO strings, so the narrower type is the accurate one.
  @Str(null) declare start_date: string | null;
  @Str(null) declare completion_date: string | null;
  @Num(0) declare score: UserCourseAttr<'score'>;
  @Num(0) declare time_accumulator: UserCourseAttr<'time_accumulator'>;
  // Stored as JSON on the backend; the mutator below parses it into a shape
  // Strapi cannot describe, so this stays hand-typed.
  @Attr(null) declare progress: Partial<UserCourseProgress> | null;
  @Bool(false) declare is_favorite: UserCourseAttr<'is_favorite'>;
  @Bool(true) declare is_assigned: UserCourseAttr<'is_assigned'>;
  @Str(null) declare unassigned_at: string | null;

  @HasOne(() => Course, 'user_course_id') declare course: Course | null;
  @HasOne(() => User, 'user_id') declare user_id: User | null;

  static override mutators() {
    return {
      progress: {
        get: (value: string | null) =>
          !value ? {} : (JSON.parse(value) as UserCourseProgress),
        set: (value: UserCourseProgress) => JSON.stringify(value),
      },
    };
  }
}
